import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { trimValidator } from '../../helpers';
import { isSupportedCurrency, normalizePriceAmount } from './price-field.utils';

export type CustomFieldInputType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'price'
  | 'phone'
  | 'email'
  | 'link'
  | 'date'
  | 'select'
  | 'checkbox';

export const CUSTOM_FIELD_TEXT_MAX_LENGTH = 128;
export const CUSTOM_FIELD_TEXTAREA_MAX_LENGTH = 5000;

export const customFieldSingleLineValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (value === null || value === undefined || value === '') return null;
  return /[\r\n]/.test(String(value)) ? { singleLine: true } : null;
};

export const customFieldNumberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (value === null || value === undefined || value === '') return null;
  return /^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(String(value).trim()) ? null : { number: true };
};

export const customFieldPriceValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    const currency = (value as Record<string, unknown>)['currency'];
    const amount = (value as Record<string, unknown>)['amount'];
    if (!isSupportedCurrency(currency)) return { currency: true };
    if (amount === null || amount === undefined || amount === '') return null;
    return normalizePriceAmount(amount) !== null ? null : { price: true };
  }
  return normalizePriceAmount(value) !== null ? null : { price: true };
};

export const customFieldPriceRequiredValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const amount = (value as Record<string, unknown>)['amount'];
    return amount === null || amount === undefined || amount === '' ? { required: true } : null;
  }
  return value === null || value === undefined || value === '' ? { required: true } : null;
};

export const PHONE_INPUT_ALLOWED_PATTERN = /^[\d+\s()-]*$/;

export function sanitizePhoneInputValue(value: unknown): string {
  return toStringValue(value).replace(/[^\d+\s()-]/g, '');
}

export function normalizePhonePayloadValue(value: unknown): string {
  const digits = toStringValue(value).replace(/\D/g, '');
  return digits ? `+${digits}` : '';
}

export const customFieldPhoneValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = String(control.value ?? '');
  if (!value.trim()) return null;
  return PHONE_INPUT_ALLOWED_PATTERN.test(value) ? null : { phone: true };
};

export const customFieldEmailValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = String(control.value ?? '').trim();
  if (!value) return null;
  return value.length <= 254 && /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i.test(value)
    ? null
    : { email: true };
};

export const customFieldLinkValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = String(control.value ?? '').trim();
  if (!value) return null;
  if (value.length > 2048 || /\s/.test(value)) return { link: true };
  const normalized = /^[a-z][a-z\d+.-]*:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(normalized);
    return ['http:', 'https:'].includes(url.protocol) ? null : { link: true };
  } catch {
    return { link: true };
  }
};

export function getCustomFieldValidators(inputType: string, isRequired = false): ValidatorFn[] {
  const required = isRequired && inputType !== 'checkbox' ? [Validators.required] : [];
  if (inputType === 'text') return [Validators.maxLength(CUSTOM_FIELD_TEXT_MAX_LENGTH), customFieldSingleLineValidator, ...(isRequired ? [Validators.required, trimValidator] : [])];
  if (inputType === 'textarea') return [Validators.maxLength(CUSTOM_FIELD_TEXTAREA_MAX_LENGTH), ...(isRequired ? [Validators.required, trimValidator] : [])];
  if (inputType === 'number') return [customFieldNumberValidator, ...required];
  if (inputType === 'price') return [customFieldPriceValidator, ...(isRequired ? [customFieldPriceRequiredValidator] : [])];
  if (inputType === 'phone') return [customFieldPhoneValidator, ...required];
  if (inputType === 'email') return [customFieldEmailValidator, ...required];
  if (inputType === 'link') return [customFieldLinkValidator, ...required];
  if (inputType === 'date' || inputType === 'select') return required;
  return [];
}

export function getCustomFieldMaxLength(inputType: string): number {
  if (inputType === 'text') return CUSTOM_FIELD_TEXT_MAX_LENGTH;
  if (inputType === 'textarea') return CUSTOM_FIELD_TEXTAREA_MAX_LENGTH;
  return 256;
}

export function normalizeCustomFieldValue(inputType: string, value: unknown): unknown {
  if (value === null || value === undefined || value === '') return value;
  const stringValue = toStringValue(value);
  if (inputType === 'number') return stringValue.trim().replace(/^(-?)\./, '$10.').replace(/\.$/, '');
  if (inputType === 'price') {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const priceValue = value as Record<string, unknown>;
      const amountValue = priceValue['amount'];
      const hasAmount = amountValue !== null && amountValue !== undefined && amountValue !== '';
      const normalizedAmount = normalizePriceAmount(amountValue);
      return {
        amount: !hasAmount ? null : normalizedAmount ?? amountValue,
        currency: priceValue['currency'],
      };
    }
    return stringValue.trim().replace(/\s+/g, '').replace(',', '.').replace(/^\./, '0.').replace(/\.$/, '');
  }
  if (inputType === 'phone') {
    return normalizePhonePayloadValue(value);
  }
  if (inputType === 'email') return stringValue.trim().toLowerCase();
  if (inputType === 'link') {
    const source = stringValue.trim();
    return !source || /\s/.test(source) || /^[a-z][a-z\d+.-]*:\/\//i.test(source) ? source : `https://${source}`;
  }
  return typeof value === 'string' ? stringValue.trim() : value;
}

function toStringValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}
